import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  listUserConsoles,
  listConsoleItems,
  createConsoleItem,
  updateConsoleItem,
  deleteConsoleItem,
  addListItem,
  updateListItem,
  deleteListItem
} from '../../redux-components/actions/consoleActions';
import Loader from '../Loader';
import Message from '../Message';
import { customSwal } from '../CustomSwal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImages, faPlus } from '@fortawesome/free-solid-svg-icons';
import ConsoleItemsBrowser from './ConsoleItemsBrowser';
import ConsoleMediaLibrary from './ConsoleMediaLibrary';
import CreateItemModal from './CreateItemModal';
import EditItemModal from './EditItemModal';
import ListItemModal from './ListItemModal';
import CustomModal from '../Modal/CustomModal';

const ConsoleComponent = () => {
  const dispatch = useDispatch();
  const [activeConsole, setActiveConsole] = useState(null);

  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [listItemModalOpen, setListItemModalOpen] = useState(false);

  // Edit/Delete states
  const [editItem, setEditItem] = useState(null);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [currentConsoleItemId, setCurrentConsoleItemId] = useState(null);
  const [editingListItem, setEditingListItem] = useState(null);
  const [mediaOpen, setMediaOpen] = useState(false);

  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  const consoleList = useSelector((state) => state.consoleList);
  const { loading: loadingConsoles, error: errorConsoles, consoles } = consoleList;

  const consoleItemCreate = useSelector((state) => state.consoleItemCreate);
  const { success: successCreateItem } = consoleItemCreate;

  const consoleItemUpdate = useSelector((state) => state.consoleItemUpdate);
  const { success: successUpdateItem } = consoleItemUpdate;

  const consoleItemDelete = useSelector((state) => state.consoleItemDelete);
  const { success: successDeleteItem } = consoleItemDelete;

  const listItemAdd = useSelector((state) => state.listItemAdd);
  const { success: successAddListItem } = listItemAdd;

  const listItemUpdate = useSelector((state) => state.listItemUpdate);
  const { success: successUpdateListItem } = listItemUpdate;

  const listItemDelete = useSelector((state) => state.listItemDelete);
  const { success: successDeleteListItem } = listItemDelete;

  // Keyed on the id, not the object: refreshUser() merges a fresh userInfo into the store on every
  // page load, so the object identity always changes even when nothing about the user did — which
  // re-ran this fetch and loaded the console list twice on mount.
  const userId = userInfo?._id;
  useEffect(() => {
    if (userId) {
      dispatch(listUserConsoles());
    }
  }, [dispatch, userId]);

  useEffect(() => {
    if (consoles && consoles.length > 0 && !activeConsole) {
      setActiveConsole(consoles[0]._id);
    }
  }, [consoles, activeConsole]);

  useEffect(() => {
    if (activeConsole) {
      dispatch(listConsoleItems(activeConsole));
    }
  }, [dispatch, activeConsole]);

  useEffect(() => {
    if (successCreateItem || successUpdateItem || successDeleteItem ||
        successAddListItem || successUpdateListItem || successDeleteListItem) {
      if (activeConsole) {
        dispatch(listConsoleItems(activeConsole));
      }
      closeAllModals();
    }
  }, [dispatch, successCreateItem, successUpdateItem, successDeleteItem,
      successAddListItem, successUpdateListItem, successDeleteListItem, activeConsole]);

  const closeAllModals = () => {
    setCreateModalOpen(false);
    setEditModalOpen(false);
    setDeleteModalOpen(false);
    setListItemModalOpen(false);
    setEditItem(null);
    setDeleteItemId(null);
    setCurrentConsoleItemId(null);
    setEditingListItem(null);
  };

  const handleConsoleChange = (e) => {
    setActiveConsole(e.target.value);
  };

  const openCreateModal = () => {
    setCreateModalOpen(true);
  };

  const openEditModal = (item) => {
    setEditItem(item);
    setEditModalOpen(true);
  };

  const openDeleteModal = (itemId) => {
    setDeleteItemId(itemId);
    setDeleteModalOpen(true);
  };

  const handleDeleteItem = () => {
    dispatch(deleteConsoleItem(deleteItemId));
  };

  const openListItemModal = (consoleItemId, listItem = null) => {
    setCurrentConsoleItemId(consoleItemId);
    setEditingListItem(listItem);
    setListItemModalOpen(true);
  };

  const handleDeleteListItem = (consoleItemId, listItemId) => {
    customSwal.fire({
      title: 'Are you sure?',
      text: 'This will delete the list item permanently.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(deleteListItem(consoleItemId, listItemId));
      }
    });
  };

  if (!userInfo) {
    return <Message>Please log in to view your console</Message>;
  }

  const currentConsole = consoles?.find((c) => c._id === activeConsole);

  return (
    <div className="w-full p-2 text-left sm:p-4">
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-2xl font-bold">Your Console</h2>
          {consoles?.length === 1 && currentConsole && (
            <p className="mt-0.5 text-sm text-gray-300">{currentConsole.title}</p>
          )}
        </div>
        {consoles?.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            {consoles.length > 1 && (
              <select
                aria-label="Select console"
                className="rounded-md bg-gray-800 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                onChange={handleConsoleChange}
                value={activeConsole || ''}
              >
                {consoles.map((console) => (
                  <option key={console._id} value={console._id}>
                    {console.title}
                  </option>
                ))}
              </select>
            )}
            <button
              type="button"
              onClick={() => setMediaOpen(true)}
              disabled={!activeConsole}
              className="inline-flex items-center gap-2 rounded-md bg-gray-700 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-600 disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faImages} />
              Media
            </button>
            <button
              type="button"
              onClick={openCreateModal}
              disabled={!activeConsole}
              className="inline-flex items-center gap-2 rounded-md bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faPlus} />
              New Item
            </button>
          </div>
        )}
      </div>

      {loadingConsoles ? (
        <Loader />
      ) : errorConsoles ? (
        <Message variant="danger">{errorConsoles}</Message>
      ) : consoles?.length === 0 ? (
        <Message>No consoles found. Please contact an administrator.</Message>
      ) : (
        activeConsole && (
          <ConsoleItemsBrowser
            activeConsole={activeConsole}
            onCreate={openCreateModal}
            onEdit={openEditModal}
            onDelete={openDeleteModal}
            onAddListItem={openListItemModal}
            onEditListItem={openListItemModal}
            onDeleteListItem={handleDeleteListItem}
          />
        )
      )}

      {/* Modals */}
      <CreateItemModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        activeConsole={activeConsole}
        dispatch={dispatch}
        createConsoleItem={createConsoleItem}
      />

      <EditItemModal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        item={editItem}
        dispatch={dispatch}
        updateConsoleItem={updateConsoleItem}
      />

      <ListItemModal
        isOpen={listItemModalOpen}
        onClose={() => setListItemModalOpen(false)}
        consoleItemId={currentConsoleItemId}
        listItem={editingListItem}
        dispatch={dispatch}
        addListItem={addListItem}
        updateListItem={updateListItem}
      />

      <ConsoleMediaLibrary
        isOpen={mediaOpen}
        onClose={() => setMediaOpen(false)}
        consoleId={activeConsole}
      />

      {/* Delete Confirmation Modal */}
      <CustomModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete Console Item"
        onConfirm={handleDeleteItem}
        confirmText="Delete"
        size="sm"
      >
        <p>Are you sure you want to delete this item? This action cannot be undone.</p>
      </CustomModal>
    </div>
  );
};

export default ConsoleComponent;
